import { create } from 'zustand';
import { getActiveWorkspaceId, setActiveWorkspaceId } from './workspace';
import { type ApiClient } from '../api/client';
import { apiClient } from '../api/default-client';
import { type IdTokenClaims } from '../auth/types';
import { deriveRoleProfile } from './permissions';
import { meResponseSchema, type MeResponse, type PermissionGrant, type RoleProfile, type SessionRole, type SessionTenant, type SessionUser, type SessionWarehouse } from './types';

export const ME_PATH = '/api/v1/identity/me';

export interface SessionState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  user: SessionUser | null;
  tenant: SessionTenant | null;
  roles: SessionRole[];
  permissions: PermissionGrant[];
  uiProfile: RoleProfile;
  warehouses: SessionWarehouse[];
  currentWarehouseId: string | null;
  /** Short technical message for logs; never render it. */
  error: string | null;
  /** What `/me` threw, for `describeError` (the screen decides the words). */
  failure: unknown;
  /** Pre-populates user/tenant/roles/permissions from id-token claims before `/me` answers. */
  hydrateFromClaims(claims: IdTokenClaims | null): void;
  /** `GET /api/v1/identity/me` */
  load(client?: ApiClient): Promise<void>;
  setCurrentWarehouse(id: string | null): void;
  clear(): void;
}

const initial = {
  status: 'idle' as SessionState['status'],
  user: null,
  tenant: null,
  roles: [] as SessionRole[],
  permissions: [] as PermissionGrant[],
  uiProfile: 'operator' as RoleProfile,
  warehouses: [] as SessionWarehouse[],
  currentWarehouseId: null,
  error: null,
  failure: null as unknown,
};

const WAREHOUSE_STORAGE_KEY = 'zagru.session.warehouse';
function isWorkspaceDenied(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { code?: unknown }).code === 'security.workspace_denied';
}

/**
 * Earlier builds kept the tenant code of the last session here so the sign-in page could show that workspace's name.
 * Nothing about a workspace is shown before sign-in any more (owner, 2026-09-25), so the value is removed, not kept.
 */
const LAST_TENANT_KEY = 'zagru.session.lastTenant';

function forgetLastTenantCode(): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.removeItem(LAST_TENANT_KEY);
  } catch {
    // storage unavailable
  }
}

function readStoredWarehouse(): string | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(WAREHOUSE_STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

function writeStoredWarehouse(id: string | null): void {
  try {
    if (typeof window === 'undefined') return;
    if (id) window.localStorage.setItem(WAREHOUSE_STORAGE_KEY, id);
    else window.localStorage.removeItem(WAREHOUSE_STORAGE_KEY);
  } catch {
    // storage unavailable
  }
}

function pickWarehouse(current: string | null, me: MeResponse): string | null {
  const ids = (me.warehouses ?? []).map((w) => w.id);
  const stored = readStoredWarehouse();
  for (const candidate of [current, stored, me.tenant.defaultWarehouseId]) {
    if (candidate && (ids.length === 0 || ids.includes(candidate))) return candidate;
  }
  return ids[0] ?? null;
}

function grantsFromClaims(claims: IdTokenClaims): PermissionGrant[] {
  const codes = Array.isArray(claims.zagru_permissions) ? claims.zagru_permissions : [];
  return codes.filter((c): c is string => typeof c === 'string').map((code) => ({ code, warehouseIds: 'ALL', partyIds: 'ALL' }));
}

function rolesFromClaims(claims: IdTokenClaims): SessionRole[] {
  const codes = Array.isArray(claims.zagru_roles) ? claims.zagru_roles : [];
  return codes.filter((c): c is string => typeof c === 'string').map((code) => ({ code, name: code }));
}

let loadSequence = 0;

export const sessionStore = create<SessionState>()((set, get) => ({
  ...initial,
  hydrateFromClaims: (claims) => {
    if (!claims) return;
    const roles = rolesFromClaims(claims);
    const state = get();
    const username = typeof claims.preferred_username === 'string' ? claims.preferred_username : claims.sub;
    const displayName = typeof claims.name === 'string' ? claims.name : username;
    set({
      user: state.user ?? { id: claims.sub, username, displayName, ...(typeof claims.email === 'string' ? { email: claims.email } : {}) },
      tenant: state.tenant ?? (typeof claims.zagru_tenant === 'string' ? { id: claims.zagru_tenant, code: claims.zagru_tenant, name: claims.zagru_tenant } : null),
      roles: state.roles.length > 0 ? state.roles : roles,
      permissions: state.permissions.length > 0 ? state.permissions : grantsFromClaims(claims),
      uiProfile: state.status === 'ready' ? state.uiProfile : deriveRoleProfile(roles),
    });
  },
  load: async (client = apiClient) => {
    const sequence = ++loadSequence;
    set({ status: 'loading', error: null, failure: null });
    try {
      let me: MeResponse;
      try {
        me = await client.get<MeResponse>(ME_PATH, { schema: meResponseSchema });
      } catch (err) {
        // W3: the remembered workspace is no longer open to this person (membership revoked, support grant ended):
        // fall back to the home workspace once instead of failing every request.
        if (!isWorkspaceDenied(err) || getActiveWorkspaceId() === null) throw err;
        setActiveWorkspaceId(null);
        me = await client.get<MeResponse>(ME_PATH, { schema: meResponseSchema });
      }
      if (sequence !== loadSequence) return;
      forgetLastTenantCode();
      set({
        status: 'ready',
        user: me.user,
        tenant: me.tenant,
        roles: me.roles,
        permissions: me.permissions,
        uiProfile: deriveRoleProfile(me.roles, me.uiProfile),
        warehouses: me.warehouses ?? [],
        currentWarehouseId: pickWarehouse(get().currentWarehouseId, me),
        error: null,
        failure: null,
      });
    } catch (err) {
      if (sequence !== loadSequence) return;
      set({ status: 'error', error: err instanceof Error ? err.message : String(err), failure: err });
    }
  },
  setCurrentWarehouse: (id) => {
    writeStoredWarehouse(id);
    set({ currentWarehouseId: id });
  },
  clear: () => set({ ...initial }),
}));

/** Resets the store to its pristine state (tests). */
export function resetSessionStore(): void {
  loadSequence += 1;
  sessionStore.setState({ ...initial });
}
